import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Plus, Trash2, Loader2, Settings } from 'lucide-react';
import {
  PERMISSIONS,
  createOrgNodeSchema,
  assignOrgDesignationSchema,
  type ApiError,
  type OrgNode,
  type CreateOrgNodeInput,
  type AssignOrgDesignationInput,
} from '@pms/shared';
import { orgService } from '@/services/org.service';
import { designationsService } from '@/services/designations.service';
import { usersService } from '@/services/users.service';
import { usePermission } from '@/hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { OrgChart } from '@/components/org-chart/OrgChart';
import { OrgNodeDrawer } from '@/components/org-chart/OrgNodeDrawer';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

interface FlatNode { node: OrgNode; depth: number; path: string }
function flatten(nodes: OrgNode[], depth = 0, prefix = ''): FlatNode[] {
  const out: FlatNode[] = [];
  for (const n of nodes) {
    const path = prefix ? `${prefix} → ${n.name}` : n.name;
    out.push({ node: n, depth, path });
    if (n.children?.length) out.push(...flatten(n.children, depth + 1, path));
  }
  return out;
}

export function OrganizationPage() {
  const queryClient = useQueryClient();
  const { has } = usePermission();
  const canManage = has(PERMISSIONS.ORG_MANAGE);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [nodeModal, setNodeModal] = useState<{ mode: 'create' | 'edit'; node?: OrgNode; parentId?: string | null } | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [levelTypesOpen, setLevelTypesOpen] = useState(false);
  const [deleteNodeConfirm, setDeleteNodeConfirm] = useState(false);

  const treeQuery = useQuery({
    queryKey: ['org', 'tree'],
    queryFn: () => orgService.getTree(),
  });

  const nodeQuery = useQuery({
    queryKey: ['org', 'node', selectedId],
    queryFn: () => orgService.getNode(selectedId!),
    enabled: !!selectedId,
  });

  const tree = treeQuery.data ?? [];
  const flat = useMemo(() => flatten(tree), [tree]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['org', 'tree'] });
    if (selectedId) void queryClient.invalidateQueries({ queryKey: ['org', 'node', selectedId] });
  };

  const deleteNode = useMutation({
    mutationFn: (id: string) => orgService.deleteNode(id),
    onSuccess: (_d, id) => {
      toast.success('Node deleted');
      if (selectedId === id) setSelectedId(null);
      invalidate();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Organization Management</h1>
          <p className="text-sm text-muted-foreground">Manage hierarchy, nodes, and designation assignments.</p>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setLevelTypesOpen(true)}>
              <Settings className="h-4 w-4" /> Level Types
            </Button>
            <Button onClick={() => setNodeModal({ mode: 'create', parentId: null })}>
              <Plus className="h-4 w-4" /> Add Root Node
            </Button>
          </div>
        )}
      </div>

      {/* Chart */}
      <div className="org-chart overflow-hidden rounded-lg border border-border/60 bg-card">
        {treeQuery.isLoading ? (
          <div className="flex h-[70vh] items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : treeQuery.isError ? (
          <div className="flex h-[70vh] flex-col items-center justify-center gap-2 px-6 text-center text-destructive">
            <span className="text-sm font-medium">{getErrorMessage(treeQuery.error)}</span>
            <Button size="sm" variant="outline" onClick={() => treeQuery.refetch()}>Try again</Button>
          </div>
        ) : tree.length === 0 ? (
          <div className="flex h-[70vh] flex-col items-center justify-center gap-1 px-6 text-center text-sm text-muted-foreground">
            <p>No nodes yet.</p>
            {canManage && <p>Click “Add Root Node” to start.</p>}
          </div>
        ) : (
          <div className="h-[calc(100vh-13rem)] min-h-[32rem] w-full">
            <OrgChart
              tree={tree}
              selectedId={selectedId}
              onSelect={setSelectedId}
              search={search}
              onSearchChange={setSearch}
            />
          </div>
        )}
      </div>

      <OrgNodeDrawer
        open={!!selectedId}
        onOpenChange={(o) => { if (!o) setSelectedId(null); }}
        detail={nodeQuery.data}
        isLoading={nodeQuery.isLoading}
        isError={nodeQuery.isError}
        onRetry={() => { void nodeQuery.refetch(); }}
        canManage={canManage}
        onAssign={() => setAssignOpen(true)}
        onAddChild={() => { if (nodeQuery.data) setNodeModal({ mode: 'create', parentId: nodeQuery.data.id }); }}
        onEdit={() => { if (nodeQuery.data) setNodeModal({ mode: 'edit', node: nodeQuery.data }); }}
        onDelete={() => { if (nodeQuery.data) setDeleteNodeConfirm(true); }}
        onRemoveDesignation={async (assignId) => {
          if (!nodeQuery.data) return;
          try {
            await orgService.removeDesignation(nodeQuery.data.id, assignId);
            toast.success('Removed');
            invalidate();
          } catch (e) { toast.error(getErrorMessage(e)); }
        }}
      />

      {nodeModal && (
        <OrgNodeModal
          mode={nodeModal.mode}
          node={nodeModal.node}
          presetParentId={nodeModal.parentId}
          allNodes={flat}
          onClose={() => setNodeModal(null)}
          onSaved={(id) => { setNodeModal(null); invalidate(); if (id) setSelectedId(id); }}
        />
      )}

      {assignOpen && selectedId && (
        <AssignDesignationModal
          nodeId={selectedId}
          onClose={() => setAssignOpen(false)}
          onSaved={() => { setAssignOpen(false); invalidate(); }}
        />
      )}

      {levelTypesOpen && (
        <ManageLevelTypesModal onClose={() => setLevelTypesOpen(false)} />
      )}

      <ConfirmDialog
        open={deleteNodeConfirm}
        onOpenChange={(o) => { if (!o) setDeleteNodeConfirm(false); }}
        title="Delete this node?"
        description="All child nodes and designation assignments will also be removed. This cannot be undone."
        confirmLabel="Delete"
        onConfirm={() => { if (nodeQuery.data) { deleteNode.mutate(nodeQuery.data.id); setDeleteNodeConfirm(false); } }}
      />
    </div>
  );
}

// ── Manage Level Types modal ─────────────────────────────────────────────────

function ManageLevelTypesModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState('');
  const [pendingDeleteType, setPendingDeleteType] = useState<{ id: string; name: string } | null>(null);

  const typesQuery = useQuery({
    queryKey: ['org', 'level-types'],
    queryFn: () => orgService.listLevelTypes(),
  });

  const invalidateTypes = () =>
    queryClient.invalidateQueries({ queryKey: ['org', 'level-types'] });

  const createMutation = useMutation({
    mutationFn: () => orgService.createLevelType(newName.trim()),
    onSuccess: () => { toast.success('Level type added'); setNewName(''); void invalidateTypes(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => orgService.deleteLevelType(id),
    onSuccess: () => { toast.success('Level type deleted'); void invalidateTypes(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const types = typesQuery.data ?? [];

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Level Types</DialogTitle>
        </DialogHeader>

        <div className="space-y-0">
          {typesQuery.isLoading ? (
            <p className="py-4 text-center text-sm text-muted-foreground">Loading…</p>
          ) : types.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">No level types yet.</p>
          ) : (
            types.map((t) => (
              <div key={t.id} className="flex items-center justify-between border-b border-border/40 py-2.5">
                <span className="text-sm text-foreground">{t.name}</span>
                <button
                  onClick={() => setPendingDeleteType({ id: t.id, name: t.name })}
                  disabled={deleteMutation.isPending}
                  className="rounded p-1 text-destructive hover:bg-destructive/10"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); if (newName.trim()) createMutation.mutate(); }}
          className="flex items-center gap-2 pt-2"
        >
          <Input
            placeholder="New level type…"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="h-9"
          />
          <Button type="submit" variant="outline" disabled={!newName.trim() || createMutation.isPending}>
            {createMutation.isPending ? 'Adding…' : 'Add'}
          </Button>
        </form>
      </DialogContent>

      <ConfirmDialog
        open={!!pendingDeleteType}
        onOpenChange={(o) => { if (!o) setPendingDeleteType(null); }}
        title={`Delete "${pendingDeleteType?.name}"?`}
        description="This level type will be permanently removed."
        confirmLabel="Delete"
        onConfirm={() => { if (pendingDeleteType) deleteMutation.mutate(pendingDeleteType.id); }}
      />
    </Dialog>
  );
}

// ── Create/Edit node modal ───────────────────────────────────────────────────

function OrgNodeModal({
  mode, node, presetParentId, allNodes, onClose, onSaved,
}: {
  mode: 'create' | 'edit';
  node?: OrgNode;
  presetParentId?: string | null;
  allNodes: FlatNode[];
  onClose: () => void;
  onSaved: (id?: string) => void;
}) {
  const levelTypesQuery = useQuery({ queryKey: ['org', 'level-types'], queryFn: () => orgService.listLevelTypes() });
  const usersQuery = useQuery({
    queryKey: ['staff', { limit: 100 }],
    queryFn: () => usersService.list({ limit: 100, is_active: true }),
  });

  const [empId, setEmpId] = useState('');

  const { register, handleSubmit, control, setValue, formState: { errors } } = useForm<CreateOrgNodeInput>({
    resolver: zodResolver(createOrgNodeSchema),
    defaultValues: {
      name: node?.name ?? '',
      level_type_id: node?.level_type_id ?? '',
      parent_id: mode === 'edit' ? (node?.parent_id ?? null) : (presetParentId ?? null),
      sort_order: node?.sort_order ?? 0,
      description: node?.description ?? '',
    },
  });

  // Edit mode: preselect the employee whose full name matches the node's current name
  useEffect(() => {
    if (mode === 'edit' && node && usersQuery.data?.data) {
      const match = usersQuery.data.data.find(
        (u) => `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() === node.name,
      );
      if (match) setEmpId(match.id);
    }
  }, [mode, node, usersQuery.data]);

  const save = useMutation({
    mutationFn: (input: CreateOrgNodeInput) =>
      mode === 'edit' && node
        ? orgService.updateNode(node.id, input).then(() => ({ id: node.id }))
        : orgService.createNode(input),
    onSuccess: (res) => {
      toast.success(mode === 'edit' ? 'Node updated' : 'Node created');
      onSaved(res?.id);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Exclude self when editing (can't parent to self)
  const parentOptions = allNodes.filter((f) => f.node.id !== node?.id);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === 'edit' ? 'Edit Node' : 'Create Node'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit((v) => save.mutate(v))} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Employee</Label>
            <input type="hidden" {...register('name')} />
            <Select
              value={empId}
              onValueChange={(id) => {
                const u = usersQuery.data?.data.find((x) => x.id === id);
                if (u) {
                  setEmpId(id);
                  setValue('name', `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim(), { shouldValidate: true });
                }
              }}
            >
              <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>
                {(usersQuery.data?.data ?? []).map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.first_name} {u.last_name}{u.employee_code ? ` (${u.employee_code})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.name && <p className="text-xs text-destructive">Please select an employee</p>}
          </div>

          <div className="space-y-1.5">
            <Label>Type</Label>
            <Controller
              control={control}
              name="level_type_id"
              render={({ field }) => (
                <Select value={field.value || ''} onValueChange={field.onChange}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {(levelTypesQuery.data ?? []).map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.level_type_id && <p className="text-xs text-destructive">Type is required</p>}
          </div>

          <div className="space-y-1.5">
            <Label>Parent Node</Label>
            <Controller
              control={control}
              name="parent_id"
              render={({ field }) => (
                <Select
                  value={field.value ?? 'root'}
                  onValueChange={(v) => field.onChange(v === 'root' ? null : v)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="root">— Root Level —</SelectItem>
                    {parentOptions.map((f) => (
                      <SelectItem key={f.node.id} value={f.node.id}>
                        {' '.repeat(f.depth * 2)}{f.node.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="sort_order">Order</Label>
            <Input id="sort_order" type="number" min={0} {...register('sort_order')} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description">Description</Label>
            <Textarea id="description" rows={3} {...register('description')} />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : mode === 'edit' ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Assign designation modal ─────────────────────────────────────────────────

function AssignDesignationModal({
  nodeId, onClose, onSaved,
}: {
  nodeId: string; onClose: () => void; onSaved: () => void;
}) {
  const { handleSubmit, control, formState: { errors } } = useForm<AssignOrgDesignationInput>({
    resolver: zodResolver(assignOrgDesignationSchema),
    defaultValues: { designation_id: '', user_id: '' },
  });

  const desigsQuery = useQuery({
    queryKey: ['designations', { limit: 100 }],
    queryFn: () => designationsService.list({ limit: 100 }),
  });
  const usersQuery = useQuery({
    queryKey: ['staff', { limit: 100 }],
    queryFn: () => usersService.list({ limit: 100, is_active: true }),
  });

  const save = useMutation({
    mutationFn: (input: AssignOrgDesignationInput) => orgService.assignDesignation(nodeId, input),
    onSuccess: () => { toast.success('Designation assigned'); onSaved(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Assign Designation</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit((v) => save.mutate(v))} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Designation</Label>
            <Controller
              control={control}
              name="designation_id"
              render={({ field }) => (
                <Select value={field.value || ''} onValueChange={field.onChange}>
                  <SelectTrigger><SelectValue placeholder="Select designation" /></SelectTrigger>
                  <SelectContent>
                    {(desigsQuery.data?.data ?? []).map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name} ({d.code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.designation_id && <p className="text-xs text-destructive">Required</p>}
          </div>

          <div className="space-y-1.5">
            <Label>Employee</Label>
            <Controller
              control={control}
              name="user_id"
              render={({ field }) => (
                <Select value={field.value || ''} onValueChange={field.onChange}>
                  <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>
                    {(usersQuery.data?.data ?? []).map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.first_name} {u.last_name}{u.employee_code ? ` (${u.employee_code})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.user_id && <p className="text-xs text-destructive">Required</p>}
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Assigning…' : 'Assign'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
